import React, { forwardRef, useImperativeHandle, useState } from "react";
import {
  Box,
  InputLabel,
  SxProps,
  Theme,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@mui/material";
import ButtonComponent from "components/button/ButtonComponent";
import TextFieldComponent, { TextFieldRef } from "components/input/TextFieldComponent";
import Notify from "config/notify";
import { SELECTED_DEFAULT } from "constants/format";
import CarTiresizeInput, { CarTiresizeInputRef } from "./CarTiresizeInput";
import { TireSize, TiresizeAspectRatio, TiresizeRimDiam, TiresizeWidth } from "../types/tire";

interface TireSizeFormProps {
  onSubmit: () => void;
  isCustom?: boolean;
  label: string;
  isDisplayNote?: boolean;
  sx?: SxProps<Theme>;
  onCustomChange?: (isCustom: boolean) => void;
  widthList?: TiresizeWidth[];
  aspectRatioList?: TiresizeAspectRatio[];
  rimDiamList?: TiresizeRimDiam[];
  selectedTire?: TireSize;
  setSelectedTire: (tire: TireSize) => void;
}

export interface CarTiresizeFormRef {
  validate: () => boolean;
}

/**
 * CarTiresizeForm component renders a form for inputting car tire size details.
 * @param {object} props - The properties object.
 * @param {Function} props.onSubmit - The function to call when the form is submitted.
 * @param {string} props.label - The label to display at the top of the form.
 * @param {object} [props.sx] - The style object to customize the form's appearance.
 * @param {boolean} [props.isDisplayNote] - Flag to determine if the note should be displayed.
 * @param {Function} [props.onCustomChange] - The function to call when the custom checkbox value changes.
 * @returns {Element} The rendered CarTiresizeForm component.
 */
const CarTiresizeForm = forwardRef<CarTiresizeFormRef, TireSizeFormProps>(
  (
    {
      onSubmit,
      label,
      sx = {},
      isDisplayNote = false,
      onCustomChange,
      widthList,
      aspectRatioList,
      rimDiamList,
      selectedTire,
      setSelectedTire,
      isCustom,
    }: TireSizeFormProps,
    ref,
  ) => {
    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const widthInputRef = React.useRef<CarTiresizeInputRef>(null);
    const aspectRatioInputRef = React.useRef<CarTiresizeInputRef>(null);
    const rimDiamInputRef = React.useRef<CarTiresizeInputRef>(null);
    const loadIndexInputRef = React.useRef<TextFieldRef>(null);

    const validateInput = () => {
      let hasError = false;
      [
        widthInputRef.current,
        aspectRatioInputRef.current,
        rimDiamInputRef.current,
        loadIndexInputRef.current,
      ].forEach((itemRef) => {
        if (itemRef) {
          const error = itemRef.validate();
          if (error) {
            hasError = true;
          }
        }
      });
      return hasError;
    };

    useImperativeHandle(ref, () => ({
      validate() {
        return validateInput();
      },
    }));

    const handleSubmit = () => {
      if (errors.loadIndex) {
        Notify.setNotification({
          show: true,
          title: "荷重指数（LI）+速度記号が無効です",
        });
        return;
      }
      if (validateInput() || Object.values(errors).some((error) => error)) {
        Notify.setNotification({
          show: true,
          title: "必須項目が入力されていません",
          actionsPosition: "end",
        });
        return;
      }
      onSubmit();
    };

    const handleCustomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newIsCustom = event.target.checked;
      if (onCustomChange) {
        onCustomChange(newIsCustom);
      }
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          position: "relative",
          mb: 7,
          ...sx,
        }}
      >
        <Box
          sx={{
            fontSize: { xs: 24, md: 28, lg: 34 },
            fontWeight: "regular",
            color: "var(--black)",
            mb: 7,
            textAlign: "center",
          }}
        >
          {label}
        </Box>

        <Box sx={{ display: "flex", columnGap: "20px", alignItems: "end" }}>
          <CarTiresizeInput
            ref={widthInputRef}
            label="幅"
            width="96px"
            list={widthList}
            value={String(selectedTire?.tire_width)}
            handleChange={(e) => {
              setSelectedTire({
                ...selectedTire,
                tire_width: Number(e.target.value),
                tire_aspect_ratio: SELECTED_DEFAULT,
                tire_rim_diam: "",
              });
            }}
            validateOption={{
              required: {
                message: "",
              },
              minLength: {
                value: 3,
                message: "",
              },
              maxLength: {
                value: 3,
                message: "",
              },
              includeLatinOnly: {
                message: "",
              },
              halfWidth: {
                message: "",
              },
            }}
          />
          <Box
            sx={{
              mb: 1,
              fontSize: { xs: 20, md: 23 },
              color: "var(--black)",
            }}
          >
            /
          </Box>
          <CarTiresizeInput
            ref={aspectRatioInputRef}
            label="扁平率"
            width="130px"
            list={aspectRatioList}
            value={String(selectedTire?.tire_aspect_ratio)}
            handleChange={(e) => {
              setSelectedTire({
                ...selectedTire,
                tire_aspect_ratio: Number(e.target.value),
                tire_rim_diam: "",
              });
            }}
            validateOption={{
              required: {
                message: "",
              },
              minLength: {
                value: 2,
                message: "",
              },
              maxLength: {
                value: 2,
                message: "",
              },
              includeLatinOnly: {
                message: "",
              },
              halfWidth: {
                message: "",
              },
            }}
            onError={(error) => {
              setErrors((prevErrors) => ({
                ...prevErrors,
                tire_aspect_ratio: error,
              }));
            }}
          />
          <Box
            sx={{
              mb: 1,
              fontSize: { xs: 20, md: 23 },
              color: "var(--black)",
            }}
          >
            R
          </Box>
          <CarTiresizeInput
            ref={rimDiamInputRef}
            label="径（インチ）"
            width="138px"
            list={rimDiamList}
            value={selectedTire?.tire_rim_diam}
            handleChange={(e) => {
              setSelectedTire({
                ...selectedTire,
                tire_rim_diam: String(e.target.value),
              });
            }}
            validateOption={{
              required: {
                message: "",
              },
              // minLength: {
              //   value: 2,
              //   message: "",
              // },
              // maxLength: {
              //   value: 2,
              //   message: "",
              // },
              // includeLatinOnly: {
              //   message: "",
              // },
              // halfWidth: {
              //   message: "",
              // },
            }}
            onError={(error) => {
              setErrors((prevErrors) => ({
                ...prevErrors,
                tire_rim_diam: error,
              }));
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mt: 5,
            gap: 4,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <InputLabel
              htmlFor="荷重指数（LI）+速度記号"
              sx={{
                fontSize: { xs: 20, md: 23 },
                color: "var(--black)",
                fontWeight: "medium",
                textOverflow: "initial",
                marginX: "24px",
              }}
            >
              荷重指数（LI）+速度記号
            </InputLabel>
            <Box sx={{ position: "relative" }}>
              <TextFieldComponent
                ref={loadIndexInputRef}
                sx={{
                  width: "130px",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "9px",
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "13.5px 14px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: !errors.loadIndex
                      ? "1px solid var(--gray-250)"
                      : "1px solid var(--red-800)",
                  },
                }}
                type="text"
                validateOption={{
                  includeLatinOnly: {
                    message: "",
                  },
                  halfWidth: {
                    message: "",
                  },
                }}
                onError={(error) => {
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    loadIndex: error,
                  }));
                }}
                value={selectedTire?.load_index || ""}
                onChangeText={(e) =>
                  setSelectedTire({
                    ...selectedTire,
                    load_index: e,
                  })
                }
              />
            </Box>
          </Box>
          {/* Phase 2 */}
          {!isDisplayNote && (
            <FormControlLabel
              sx={{
                fontSize: { xs: 20, md: 23 },
                color: "var(--black)",
                opacity: !isCustom ? 0.5 : 1,
              }}
              control={
                <Checkbox disabled={!isCustom} checked={isCustom} color="secondary" onChange={handleCustomChange} />
              }
              label={
                <Typography sx={{ fontSize: { xs: 20, md: 23 }, color: "var(--black)" }}>
                  前後異径
                </Typography>
              }
            />
          )}
        </Box>
        {!isCustom && (
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: "50px" }}>
            <ButtonComponent
              theme="dark"
              label="商品選択へ進む"
              onClick={handleSubmit}
              sx={{
                fontSize: { xs: 20, md: 25 },
                width: "225px",
                marginX: "auto",
              }}
            />
          </Box>
        )}
      </Box>
    );
  },
);

export default CarTiresizeForm;
